'use server'

import { randomUUID } from 'node:crypto'

import { revalidatePath } from 'next/cache'
import { z } from 'zod'

import { getCustomerSession } from '@/features/account/services/session.server'
import { expireCatalogCache } from '@/features/catalog/services/catalog.server'
import { mutateDb, readDb } from '@/features/catalog/services/db.server'
import { maskWriterName } from '../lib/writer'

export type CommunityFormState = {
  status: 'idle' | 'error' | 'success'
  message?: string
  fieldErrors?: Record<string, string | undefined>
}

const reviewFormSchema = z.object({
  productId: z.string().min(1).max(20),
  rating: z.coerce.number().int().min(1, '별점을 선택해 주세요.').max(5),
  content: z
    .string()
    .trim()
    .min(10, '리뷰를 10자 이상 입력해 주세요.')
    .max(1000),
})

export async function submitReview(
  _prev: CommunityFormState,
  formData: FormData
): Promise<CommunityFormState> {
  const session = await getCustomerSession()
  if (!session)
    return { status: 'error', message: '로그인 후 작성할 수 있습니다.' }
  const parsed = reviewFormSchema.safeParse({
    productId: formData.get('productId'),
    rating: formData.get('rating'),
    content: formData.get('content'),
  })
  if (!parsed.success) {
    const fields = z.flattenError(parsed.error).fieldErrors
    return {
      status: 'error',
      message: '입력 내용을 확인해 주세요.',
      fieldErrors: {
        rating: fields.rating?.[0],
        content: fields.content?.[0],
      },
    }
  }
  const { productId, rating, content } = parsed.data
  const db = await readDb()
  const customer = db.customers.find((item) => item.id === session.customerId)
  if (!customer)
    return { status: 'error', message: '회원 정보를 찾을 수 없습니다.' }
  const purchased = db.orders.some(
    (order) =>
      order.customerId === customer.id &&
      order.status !== 'canceled' &&
      order.lines.some((line) => line.productId === productId)
  )
  if (!purchased) {
    return {
      status: 'error',
      message: '구매한 회원만 리뷰를 작성할 수 있습니다.',
    }
  }
  const saved = await mutateDb((store) => {
    const duplicate = store.reviews.some(
      (item) => item.productId === productId && item.customerId === customer.id
    )
    if (duplicate) return false
    store.reviews.unshift({
      id: randomUUID(),
      productId,
      source: 'shop',
      writer: maskWriterName(customer.name),
      customerId: customer.id,
      rating,
      content,
      optionText: '',
      photos: [],
      status: 'visible',
      reply: '',
      repliedAt: null,
      createdAt: new Date().toISOString(),
    })
    return true
  })
  if (!saved)
    return { status: 'error', message: '이미 이 상품에 리뷰를 작성했습니다.' }
  expireCatalogCache()
  revalidatePath(`/products/${productId}`)
  return { status: 'success', message: '리뷰를 등록했습니다.' }
}

const questionFormSchema = z.object({
  productId: z.string().min(1).max(20),
  question: z
    .string()
    .trim()
    .min(5, '문의 내용을 5자 이상 입력해 주세요.')
    .max(1000),
})

export async function submitQuestion(
  _prev: CommunityFormState,
  formData: FormData
): Promise<CommunityFormState> {
  const session = await getCustomerSession()
  if (!session)
    return { status: 'error', message: '로그인 후 작성할 수 있습니다.' }
  const parsed = questionFormSchema.safeParse({
    productId: formData.get('productId'),
    question: formData.get('question'),
  })
  if (!parsed.success) {
    const fields = z.flattenError(parsed.error).fieldErrors
    return {
      status: 'error',
      message: '입력 내용을 확인해 주세요.',
      fieldErrors: { question: fields.question?.[0] },
    }
  }
  const { productId, question } = parsed.data
  const isSecret = formData.get('isSecret') === 'on'
  const saved = await mutateDb((store) => {
    const customer = store.customers.find(
      (item) => item.id === session.customerId
    )
    if (!customer || !store.products.some((item) => item.id === productId)) {
      return false
    }
    store.questions.unshift({
      id: randomUUID(),
      productId,
      source: 'shop',
      writer: maskWriterName(customer.name),
      customerId: customer.id,
      question,
      answer: '',
      answeredAt: null,
      isSecret,
      status: 'visible',
      createdAt: new Date().toISOString(),
    })
    return true
  })
  if (!saved) return { status: 'error', message: '문의를 등록하지 못했습니다.' }
  expireCatalogCache()
  revalidatePath(`/products/${productId}`)
  return {
    status: 'success',
    message: '문의를 등록했습니다. 답변이 달리면 여기에 표시됩니다.',
  }
}
